// DataTable — generic table primitive (CHG-1225).
//
// Slim subset of v1 (10 column types vs v1's 18). Per
// feedback_composition_system.md — never use raw <table> in pages; always
// go through this primitive. Cell renderer extracted to DataTableCell.tsx
// to keep this file ≤180 LOC per PM's slim-primitive target.
//
// CHG-1330 / ITER-3457 — opt-in selectable mode (closes DRIFT-648). When
// `selectable` is true, the table injects a checkbox column at index 0
// with a header select-all (current page) and per-row checkboxes; selection
// is controlled via `selectedIds` + `onSelectionChange`. Existing
// non-selectable callers are unaffected.
//
// CHG-1423 / ITER-3543 — DRIFT-656 close. Horizontal-scroll for wide
// tables on narrow viewports. The outer card previously used
// `overflow: hidden` which clipped wide tables on phones — every list
// page crammed cells to <60px width and truncated content. The wrapper
// now uses `overflow-x: auto` and the table carries
// `min-width: max-content`, so tables maintain their natural width and
// scroll horizontally when the container can't fit them. On wide
// screens (container ≥ table natural width) the `width: 100%` stretches
// the table as before — no visual change on desktop. No new prop;
// behavior is strictly better than the prior clip-and-hope default.

import { useFormatters } from "../../contexts/IntlContext";
import { renderCell } from "./DataTableCell";
import { getStageLabel, type Entity } from "../../lib/stage-labels";
import type { ReactNode } from "react";

// Tone token → tint utility class (kept local; mirrors the table in
// StageAmbientRow.tsx). DataTable applies tint inline on <tr> rather than
// wrapping in <StageAmbientRow> because <div> inside <tbody> is invalid HTML.
const TONE_TINT_CLASS: Record<string, string> = {
  "g-home": "tint-home", "g-people": "tint-people", "g-giving": "tint-giving",
  "g-events": "tint-events", "g-channels": "tint-channels", "g-imports": "tint-imports",
  "g-automation": "tint-automation", "g-communications": "tint-communications",
  "g-settings": "tint-settings", "g-mygiving": "tint-mygiving",
};

// Warm tint applied to selected rows (CHG-1330). Subtle amber surface,
// not the cold-blue browser default. Lives inline because there's no
// `tint-selected` utility class — selection isn't an ambient-row tone.
const SELECTED_ROW_BG = "rgba(245, 158, 11, 0.08)"; // amber-500 at 8% opacity

export type DataTableColumnType =
  | "text" | "number" | "currency" | "date" | "datetime" | "relative-date"
  | "email" | "stage-chip" | "actions" | "custom"
  // CHG-1666 / ITER-3778 — three new built-in cell types for the
  // doctrine-correct Donors list enrichment. DataTableCell handles each
  // case so screen configs stay declarative (no per-page helper).
  | "avatar-name"
  | "lifecycle-badge"
  | "chip-list";

export interface DataTableColumn<T> {
  field: string;
  label: string;
  type: DataTableColumnType;
  width?: string;
  sortable?: boolean;
  currencyField?: string;
  entity?: Entity;
  render?: (row: T) => ReactNode;
  actions?: Array<{ id: string; label: string; onClick: (row: T) => void }>;
}

export interface DataTableProps<T extends { id: string | number }> {
  data: T[];
  columns: DataTableColumn<T>[];
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  ambientRow?: { stageField: string; entity: Entity; activeStages: string[] };
  pagination?: { page: number; pageSize: number; total: number };
  onPageChange?: (page: number) => void;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (field: string, dir: "asc" | "desc") => void;
  searchValue?: string;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  onRowClick?: (row: T) => void;
  emptyState?: { title: string; subtitle?: string };
  /** CHG-1330 — opt-in row selection. Off by default; existing callers unaffected. */
  selectable?: boolean;
  /** Controlled selection state. Caller owns the array. */
  selectedIds?: Array<string | number>;
  /** Fires on per-row toggle + header select-all. */
  onSelectionChange?: (ids: Array<string | number>) => void;
}

export function DataTable<T extends { id: string | number }>(props: DataTableProps<T>) {
  const fmt = useFormatters();
  const { data, columns, isLoading, error, onRetry, ambientRow, pagination,
    onPageChange, sortField, sortDirection, onSort,
    searchValue, searchPlaceholder, onSearchChange, onRowClick, emptyState,
    selectable, selectedIds, onSelectionChange } = props;

  const selectedSet = new Set<string | number>(selectedIds ?? []);
  const visibleIds = data.map((r) => r.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedSet.has(id));

  const toggleRow = (id: string | number) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange(Array.from(next));
  };
  const toggleAllVisible = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedSet);
    if (allVisibleSelected) {
      visibleIds.forEach((id) => next.delete(id));
    } else {
      visibleIds.forEach((id) => next.add(id));
    }
    onSelectionChange(Array.from(next));
  };

  const totalCols = columns.length + (selectable ? 1 : 0);

  if (error) {
    return (
      <div className="card" style={{ padding: 24, textAlign: "center" }}>
        <div style={{ color: "var(--color-error)", marginBottom: 8 }}>{error.message}</div>
        {onRetry ? <button type="button" className="btn btn-secondary" onClick={onRetry}>Retry</button> : null}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {onSearchChange ? (
        <input type="search" className="input" placeholder={searchPlaceholder ?? "Search…"}
          value={searchValue ?? ""} onChange={(e) => onSearchChange(e.target.value)}
          style={{ maxWidth: 320 }} />
      ) : null}

      <div
        data-testid="datatable-scroll-wrapper"
        className="card"
        style={{ padding: 0, overflowX: "auto", overflowY: "hidden" }}
      >
        <table
          data-testid="datatable-table"
          style={{
            width: "100%",
            minWidth: "max-content",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <thead>
            <tr>
              {selectable ? (
                <th style={{ width: 36, padding: "10px 14px", background: "var(--color-bg-subtle)",
                    borderBottom: "1px solid var(--color-border-subtle)", textAlign: "left" }}>
                  <input type="checkbox"
                    aria-label="Select all rows on this page"
                    data-testid="datatable-select-all"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleAllVisible} />
                </th>
              ) : null}
              {columns.map((c) => (
                <th key={c.field}
                  style={{ textAlign: "left", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                    letterSpacing: "0.06em", color: "var(--color-text-muted)", padding: "10px 14px",
                    background: "var(--color-bg-subtle)", borderBottom: "1px solid var(--color-border-subtle)",
                    cursor: c.sortable ? "pointer" : "default", width: c.width }}
                  onClick={c.sortable && onSort
                    ? () => onSort(c.field, sortField === c.field && sortDirection === "asc" ? "desc" : "asc")
                    : undefined}>
                  {c.label}{sortField === c.field ? (sortDirection === "asc" ? " ↑" : " ↓") : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={totalCols} style={{ padding: 24, textAlign: "center", color: "var(--color-text-muted)" }}>Loading…</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={totalCols} style={{ padding: 32, textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{emptyState?.title ?? "No results"}</div>
                {emptyState?.subtitle ? <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{emptyState.subtitle}</div> : null}
              </td></tr>
            ) : (
              data.map((row) => {
                const stageId = ambientRow ? String((row as Record<string, unknown>)[ambientRow.stageField] ?? "") : "";
                const isAmbient = !!(ambientRow && ambientRow.activeStages.includes(stageId));
                const tone = isAmbient && ambientRow
                  ? getStageLabel(stageId, ambientRow.entity).tone_color
                  : null;
                const tintClass = tone ? TONE_TINT_CLASS[tone] ?? "" : "";
                const isSelected = selectable && selectedSet.has(row.id);
                return (
                  <tr key={row.id} onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={tintClass || undefined}
                    style={{
                      cursor: onRowClick ? "pointer" : "default",
                      borderLeft: tone ? `3px solid var(--color-${tone}-from)` : undefined,
                      background: isSelected ? SELECTED_ROW_BG : undefined,
                    }}
                    data-row-id={row.id} data-stage={isAmbient ? stageId : undefined}
                    data-tone={tone ?? undefined}
                    data-selected={isSelected ? "true" : undefined}>
                    {selectable ? (
                      <td style={{ padding: "12px 14px", borderBottom: "1px solid var(--color-border-subtle)" }}
                        onClick={(e) => { e.stopPropagation(); }}>
                        <input type="checkbox"
                          aria-label={`Select row ${row.id}`}
                          data-testid={`datatable-select-row-${row.id}`}
                          checked={!!isSelected}
                          onChange={() => toggleRow(row.id)} />
                      </td>
                    ) : null}
                    {columns.map((c) => (
                      <td key={c.field} style={{ padding: "12px 14px", borderBottom: "1px solid var(--color-border-subtle)", fontSize: 13 }}>
                        {renderCell(c, row, fmt)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.total > pagination.pageSize ? (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "var(--color-text-muted)" }}>
          <span>{pagination.page * pagination.pageSize + 1}-{Math.min((pagination.page + 1) * pagination.pageSize, pagination.total)} of {pagination.total}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={pagination.page === 0}
              onClick={() => onPageChange?.(pagination.page - 1)}>Prev</button>
            <button type="button" className="btn btn-secondary btn-sm"
              disabled={(pagination.page + 1) * pagination.pageSize >= pagination.total}
              onClick={() => onPageChange?.(pagination.page + 1)}>Next</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
